import React, { useEffect, useState } from "react";
import CreateClientDialog from "../components/customers/CreateClientDialog";
import "../styles/clients.css";

const API_URL = "https://crmapi.zauntrip.com/api/clients";

const STATUS_STYLE = {
  New:       { background: "#e3f2fd", color: "#1976d2" },
  Active:    { background: "#e8f5e9", color: "#2e7d32" },
  Converted: { background: "#fff3e0", color: "#f57c00" },
  Inactive:  { background: "#f5f5f5", color: "#757575" },
};

export default function Clients() {
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [openDialog, setOpenDialog]   = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res  = await fetch(API_URL);
      const data = await res.json();
      setClients(data.success && Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Fetch clients error:", err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  /* Accepts FormData (multipart) from dialog */
  const handleSave = async (formData) => {
    const isEdit = !!editingClient;
    const url    = isEdit ? `${API_URL}/${editingClient._id}` : API_URL;
    const method = isEdit ? "PUT" : "POST";

    try {
      setSaving(true);
      // Do NOT set Content-Type — browser sets it with boundary for multipart
      const response = await fetch(url, { method, body: formData });
      const result   = await response.json();

      if (!result.success) { alert(result.message || "Operation failed"); return; }
      setOpenDialog(false);
      setEditingClient(null);
      fetchClients();
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      const res    = await fetch(`${API_URL}/${id}`, { method: "DELETE" });
      const result = await res.json();
      if (result.success) fetchClients();
      else alert(result.message || "Failed to delete client");
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete client");
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Clients</h1>
        <button
          className="btn-primary"
          onClick={() => { setEditingClient(null); setOpenDialog(true); }}
        >
          + Add Client
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : clients.length === 0 ? (
        <div className="empty-state"><p>No clients found. Add your first client!</p></div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Nationality</th>
              <th>Passport No.</th>
              <th>Passport Expiry</th>
              <th>Source</th>
              <th>Status</th>
              <th>Booking Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const statusStyle = STATUS_STYLE[c.status] || {};
              const passportExpiry = c.passportExpiryDate
                ? new Date(c.passportExpiryDate).toLocaleDateString("en-IN")
                : "—";
              const isExpiringSoon = c.passportExpiryDate
                && (new Date(c.passportExpiryDate) - new Date()) < 180 * 24 * 60 * 60 * 1000
                && new Date(c.passportExpiryDate) > new Date();
              const isExpired = c.passportExpiryDate && new Date(c.passportExpiryDate) < new Date();

              return (
                <tr key={c._id}>
                  <td>
                    {c.title} {c.firstName} {c.lastName}
                  </td>
                  <td>{c.email || "—"}</td>
                  <td>{c.phone || "—"}</td>
                  <td>{c.nationality || "—"}</td>
                  <td><code style={{ fontSize: 12 }}>{c.passportNumber || "—"}</code></td>
                  <td>
                    <span style={{
                      color: isExpired ? "#dc2626" : isExpiringSoon ? "#d97706" : "#111827",
                      fontWeight: (isExpired || isExpiringSoon) ? 600 : 400,
                      fontSize: 13,
                    }}>
                      {isExpired ? "⚠️ " : isExpiringSoon ? "⏳ " : ""}{passportExpiry}
                    </span>
                  </td>
                  <td>{c.source || "—"}</td>
                  <td>
                    <span style={{
                      ...statusStyle,
                      padding: "3px 10px",
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                    }}>
                      {c.status || "New"}
                    </span>
                  </td>
                  <td>{c.bookingType === "direct_client" ? "Direct Client" : "Agent"}</td>
                  <td className="actions">
                    <button
                      className="btn-outline"
                      onClick={() => { setEditingClient(c); setOpenDialog(true); }}
                    >
                      Edit
                    </button>
                    <button className="btn-danger" onClick={() => handleDelete(c._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <CreateClientDialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); setEditingClient(null); }}
        onSubmit={handleSave}
        isLoading={saving}
        initialData={editingClient}
      />
    </div>
  );
}